/**
 * The session. Standalone, this is the piece no host is here to provide.
 *
 * The state machine is `AuthStatus` in ./context.ts, where every terminal state exists
 * because there is a distinct thing to tell a person — and, in particular, because
 * "holding a token" and "the service accepts this token" are different facts. See the
 * comment on `authenticated` there.
 *
 * The context and the hook live in ./context.ts rather than here so React Fast Refresh
 * can keep this module's state across an edit; the reason is written down there.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { setBearerToken, onUnauthorized, tokenWasSent } from '../api';
import { AuthContext, type AuthStatus, type AuthValue } from './context';
import { SHELL_CONFIG } from '../config';
import { callsForReauthentication, diagnose, type Fault } from './diagnose';
import { buildLoginUrl, buildLogoutUrl, CALLBACK_PATH, navigateTo } from './redirect';
import {
  clearSignInAttempts,
  clearToken,
  loadToken,
  recordSignInAttempt,
  saveToken,
  signInAttemptCount,
} from './store';
import { decodeToken, isExpired, readTenantClaim } from './token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const onCallbackRoute = location.pathname === CALLBACK_PATH;

  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<AuthStatus>('booting');
  const [fault, setFault] = useState<Fault | null>(null);

  // The transport reads the token through a module-level cell, so it must be updated
  // before any request goes out — not in an effect after the first render, which would
  // let the first query fire unauthenticated and produce a spurious 401.
  const publish = useCallback((next: string | null) => {
    setBearerToken(next);
    setToken(next);
  }, []);

  const signIn = useCallback(
    (returnTo?: string) => {
      if (SHELL_CONFIG.authMode === 'manual') {
        // There is no issuer to go to. Say so rather than navigating nowhere.
        publish(null);
        clearToken();
        setStatus('awaiting_token');
        return;
      }
      if (!recordSignInAttempt()) {
        // The loop guard. Somebody has to stop, and it is not going to be the service.
        setFault({
          kind: 'service_unconfigured',
          code: null,
          status: null,
          message:
            `The shell has been sent to the identity provider ${signInAttemptCount()} times ` +
            'in the last two minutes and is still being refused. Another round trip will ' +
            'not help, so it has stopped.',
        });
        setStatus('blocked');
        return;
      }
      setStatus('redirecting');
      navigateTo(buildLoginUrl(returnTo));
    },
    [publish],
  );

  const signOut = useCallback(() => {
    clearToken();
    clearSignInAttempts();
    publish(null);
    if (SHELL_CONFIG.authMode === 'manual') {
      setStatus('awaiting_token');
      setFault(null);
      return;
    }
    setStatus('redirecting');
    navigateTo(buildLogoutUrl());
  }, [publish]);

  const acceptToken = useCallback(
    (next: string) => {
      saveToken(next);
      publish(next);
      setFault(null);
      setStatus('authenticated');
    },
    [publish],
  );

  const reportFault = useCallback(
    (next: Fault) => {
      setFault(next);
      if (callsForReauthentication(next)) {
        // The credential is bad. Drop it before leaving, or the token that caused this
        // is still in storage when we come back and the loop starts one turn earlier.
        clearToken();
        publish(null);
        signIn();
        return;
      }
      if (next.kind === 'service_unconfigured' || next.kind === 'tenant_unbound') {
        setStatus('blocked');
      }
      // 'unavailable', 'unreachable', 'not_in_plan' and 'other' leave the session alone.
      // None of them is a statement about the credential.
    },
    [publish, signIn],
  );

  const confirmSession = useCallback(() => {
    clearSignInAttempts();
    setFault((current) => (current && current.kind !== 'unavailable' ? null : current));
  }, []);

  const clearFault = useCallback(() => setFault(null), []);

  // -- boot ---------------------------------------------------------------------------

  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    // The callback route owns the token on that one path. Booting here as well would
    // race it and, in redirect mode, would bounce straight back out to the issuer with
    // the answer sitting unread in the URL.
    if (onCallbackRoute) {
      booted.current = true;
      return;
    }
    booted.current = true;

    const stored = loadToken();
    if (stored && !isExpired(stored)) {
      publish(stored);
      setStatus('authenticated');
      return;
    }
    if (stored) clearToken();
    signIn();
  }, [onCallbackRoute, publish, signIn]);

  // -- 401s from anywhere -------------------------------------------------------------

  useEffect(
    () =>
      onUnauthorized((error) => {
        // `tokenWasSent()` is what separates "your credential is bad" from "this service
        // never looked at it". See auth/diagnose.ts.
        reportFault(diagnose(error, tokenWasSent()));
      }),
    [reportFault],
  );

  // A token that expires while the tab is open should go to the issuer at the moment it
  // expires, not at the next 401. One timer, re-armed whenever the token changes.
  useEffect(() => {
    if (!token || status !== 'authenticated') return undefined;
    const claims = decodeToken(token);
    if (!claims || typeof claims.exp !== 'number') return undefined;
    const msUntilExpiry = claims.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) return undefined;
    // setTimeout saturates past ~24.8 days and would fire immediately.
    const delay = Math.min(msUntilExpiry, 2_147_483_000);
    const timer = window.setTimeout(() => {
      clearToken();
      publish(null);
      signIn(window.location.pathname + window.location.search);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [token, status, publish, signIn]);

  const value = useMemo<AuthValue>(
    () => ({
      status,
      fault,
      token,
      claims: token ? decodeToken(token) : null,
      tenantClaim: readTenantClaim(token ? decodeToken(token) : null),
      signIn,
      signOut,
      acceptToken,
      reportFault,
      confirmSession,
      clearFault,
    }),
    [status, fault, token, signIn, signOut, acceptToken, reportFault, confirmSession, clearFault],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
